'use client';

import { useState } from 'react';
import { BubbleActions } from '@/components/chat/BubbleBlock';
import type { ChatTapResult } from '@/lib/chat/block-actions';

/**
 * The approval inside a component: what happens, who it reaches, the words
 * that go out, and one tap.
 *
 * Shared by every component that ends in a send — the approval stack, the
 * document card, the quote follow-up, the certificate reminders — so the
 * trust model reads the same everywhere it appears: nothing is approved that
 * has not written out what it is, to whom, and in what words. The approve tap
 * decides an EXISTING card (`decideFromChat`); this never sends on its own.
 *
 * A refusal stays on screen in the server's words with the strip offering a
 * retry (#35, #42). Approved, the strip becomes a receipt rather than
 * vanishing, so scrolling back shows what was agreed.
 */
export interface SendPreviewProps {
  /** "Email the invoice", "Text the reminder". */
  what: string;
  /** "Dee Walsh · dee@example.com". Null when the card names nobody. */
  to: string | null;
  /** The words exactly as they will go, or null for a standard document email. */
  words: string | null;
  /** The orange verb: "Send invoice". */
  verb: string;
  /** Past tense for the receipt: "Sent". */
  doneLabel: string;
  /** Second, quiet tap, e.g. "Not now". Omit for a single action. */
  laterLabel?: string;
  onApprove: () => Promise<ChatTapResult>;
  onLater?: () => void;
  /** False on an older reply, or while Mucka is mid-answer. */
  live: boolean;
}

export function SendPreview({ what, to, words, verb, doneLabel, laterLabel, onApprove, onLater, live }: SendPreviewProps) {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const approve = async () => {
    setState('busy');
    setError(null);
    const out = await onApprove();
    if (out.ok) {
      setState('done');
    } else {
      setState('error');
      setError(out.message);
    }
  };

  return (
    <div className="mchat-send">
      <dl className="mchat-send-facts">
        <div className="mchat-send-fact"><dt>What happens</dt><dd>{what}</dd></div>
        {to && <div className="mchat-send-fact"><dt>To</dt><dd>{to}</dd></div>}
      </dl>
      {words && <blockquote className="mchat-send-words">{words}</blockquote>}
      {error && <p className="mchat-body mchat-error" role="alert">{error}</p>}
      <BubbleActions
        done={state === 'done' ? doneLabel : null}
        actions={[
          {
            label: state === 'error' ? 'Try again' : verb,
            busyLabel: 'Sending…',
            busy: state === 'busy',
            disabled: !live,
            onPress: () => { void approve(); },
          },
          ...(laterLabel && onLater
            ? [{ label: laterLabel, tone: 'quiet' as const, disabled: !live || state === 'busy', onPress: onLater }]
            : []),
        ]}
      />
    </div>
  );
}
